import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { addDays, format, isAfter, isBefore, parse, startOfToday } from 'date-fns'
import { 
  CalendarIcon, 
  ClockIcon, 
  CheckCircleIcon,
  InformationCircleIcon,
  QuestionMarkCircleIcon,
  PhoneIcon,
  LightBulbIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline'
import { StarIcon } from '@heroicons/react/24/solid'
import DatePicker from 'react-datepicker'
import "react-datepicker/dist/react-datepicker.css"
import { useRef, useEffect } from 'react';
import { Helmet } from 'react-helmet-async'
import React from 'react'
import { SEO, createStructuredData } from '../components/SEO'

const schema = z.object({
  currentStage: z.enum([
    'offer_accepted',
    'attorney_review',
    'mortgage_application',
    'appraisal',
    'underwriting',
    'clear_to_close',
    'final_walkthrough',
    'closing_day',
  ]),
  currentStageDate: z.string(),
})

type FormData = z.infer<typeof schema>

interface TimelineEvent {
  id: string
  name: string
  description: string
  date: Date
  status: 'completed' | 'current' | 'upcoming'
  daysFromStart: number
  tips?: string[]
}

const stages = [
  {
    value: 'offer_accepted',
    label: 'Offer Accepted',
    description: 'Starting point of the closing process',
    tips: [
      'Review all contract terms carefully',
      'Prepare earnest money deposit',
      'Contact a real estate attorney',
      'Start gathering financial documents',
    ],
  },
  {
    value: 'attorney_review',
    label: 'Attorney Review & Inspection',
    description: '5 business days for attorney review and home inspection',
    tips: [
      'Schedule home inspection immediately',
      'Review property disclosure documents',
      'Respond to attorney modifications promptly',
      'Document any repair requests',
    ],
  },
  {
    value: 'mortgage_application',
    label: 'Mortgage Application',
    description: 'Submit formal mortgage application and required documentation',
    tips: [
      'Submit application within first 5 days',
      'Provide all requested financial documents',
      'Avoid making large purchases or opening new credit',
      'Keep copies of all submitted documents',
    ],
  },
  {
    value: 'appraisal',
    label: 'Appraisal Period',
    description: 'Professional valuation of the property (14-21 days)',
    tips: [
      'Provide comparable sales to appraiser',
      'Document recent home improvements',
      'Be prepared for potential value gaps',
      'Keep communication open with your lender',
    ],
  },
  {
    value: 'underwriting',
    label: 'Loan Processing & Underwriting',
    description: 'Loan processing and final approval (30-45 days)',
    tips: [
      'Respond quickly to lender requests',
      'Maintain consistent employment',
      'Keep all financial documents updated',
      'Monitor your credit report',
    ],
  },
  {
    value: 'clear_to_close',
    label: 'Clear to Close',
    description: 'Final approval and 3-day review period',
    tips: [
      'Review Closing Disclosure carefully',
      'Schedule final walkthrough',
      'Prepare certified funds for closing',
      'Schedule utilities transfer',
    ],
  },
  {
    value: 'final_walkthrough',
    label: 'Final Walkthrough',
    description: 'Final property inspection 1-2 days before closing',
    tips: [
      'Verify all agreed-upon repairs are completed',
      'Check all appliances and systems',
      'Document any remaining issues',
      'Take photos for documentation',
    ],
  },
  {
    value: 'closing_day',
    label: 'Closing Day',
    description: 'Final document signing and property transfer',
    tips: [
      'Bring government-issued photo ID',
      'Arrive early to review documents',
      'Bring certified funds if required',
      'Get all house keys and garage openers',
    ],
  },
]

export default function TimelineCalculator() {
  const [timeline, setTimeline] = useState<TimelineEvent[]>([])
  const [selectedStage, setSelectedStage] = useState<string | null>(null)
  const [selectedDate, setSelectedDate] = useState<Date>(startOfToday())
  const resultsRef = useRef<HTMLDivElement>(null);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      currentStageDate: format(startOfToday(), 'yyyy-MM-dd')
    }
  })

  const currentStage = watch('currentStage')
  const currentStageInfo = stages.find(stage => stage.value === currentStage)

  useEffect(() => {
    if (timeline.length > 0 && resultsRef.current) {
      resultsRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [timeline]);

  const onSubmit = (data: FormData) => {
    const startDate = parse(data.currentStageDate, 'yyyy-MM-dd', new Date())
    const events: TimelineEvent[] = []
    const today = startOfToday()

    // Updated Illinois-specific timeline guidelines for Q4 2024
    const timelineConfig = {
      offer_accepted: { days: 0, name: 'Offer Accepted' },
      attorney_review: { days: 5, name: 'Attorney Review & Inspection' },
      mortgage_application: { days: 5, name: 'Mortgage Application' },
      appraisal: { days: 26, name: 'Appraisal Period' },
      underwriting: { days: 45, name: 'Loan Processing & Underwriting' },
      clear_to_close: { days: 46, name: 'Clear to Close' },
      final_walkthrough: { days: 48, name: 'Final Walkthrough' },
      closing_day: { days: 49, name: 'Closing Day' },
    }

    // Get the days for current stage
    const currentStageDays = Object.entries(timelineConfig).find(([stage]) => stage === data.currentStage)?.[1].days || 0

    // Create timeline events
    Object.entries(timelineConfig).forEach(([stage, config]) => {
      const eventDate = addDays(startDate, config.days - currentStageDays)
      
      // Calculate status based on the event date and current stage days
      let status: 'completed' | 'current' | 'upcoming'
      if (stage === data.currentStage) {
        status = 'current'
      } else if (config.days < currentStageDays) {
        status = 'completed'
      } else {
        status = 'upcoming'
      }

      const stageInfo = stages.find(s => s.value === stage) || stages[0]

      events.push({
        id: stage,
        name: config.name,
        description: getStageDescription(stage),
        date: eventDate,
        status,
        daysFromStart: config.days - currentStageDays,
        tips: stageInfo.tips,
      })
    })

    setTimeline(events.sort((a, b) => a.daysFromStart - b.daysFromStart))
  }

  const getStageDescription = (stage: string): string => {
    switch (stage) {
      case 'offer_accepted':
        return 'The seller has accepted your offer. This is the starting point of your closing timeline.'
      case 'attorney_review':
        return 'Your attorney will review the contract and you\'ll complete the home inspection. This typically takes 5 business days in Illinois.'
      case 'mortgage_application':
        return 'Submit your formal mortgage application and required documentation. This should be done within the first 5 days after offer acceptance.'
      case 'appraisal':
        return 'The lender will order an appraisal to verify the property value. This typically takes 14-21 days to complete.'
      case 'underwriting':
        return 'Loan processing and underwriting typically takes 30-45 days, with an average of 44 days for conventional and FHA loans as of Q4 2024.'
      case 'clear_to_close':
        return 'Final lender approval followed by a mandatory 3-day review period of the Closing Disclosure.'
      case 'final_walkthrough':
        return 'Final inspection of the property 1-2 days before closing to verify condition and completed repairs.'
      case 'closing_day':
        return 'The final step where you\'ll sign all documents and receive keys to your new home. Plan for 1-2 hours to complete.'
      default:
        return ''
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-500 text-white'
      case 'current':
        return 'bg-brand-600 text-white'
      default:
        return 'bg-gray-100 text-gray-500'
    }
  }

  return (
    <div>
      <SEO 
        title="Illinois Home Buying Timeline Calculator | Purchase Process Guide"
        description="Create a personalized home buying timeline in Illinois. Free tool to map out key steps, deadlines, and milestones for a smooth home purchase."
        keywords={["home buying timeline", "Illinois real estate process", "home purchase steps", "buyer's journey", "real estate milestones"]}
        canonicalUrl="https://calculators.northsidelegal.com/timeline-calculator"
        structuredData={{
          "@type": "Calculator",
          "name": "Home Closing Timeline Calculator for Illinois Home Buyers",
          "description": "Create a comprehensive closing timeline for your home buying journey",
          "url": "https://calculators.northsidelegal.com/timeline-calculator",
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "7321 Ridgeway Avenue",
            "addressLocality": "Skokie",
            "addressRegion": "IL",
            "postalCode": "60076",
            "addressCountry": "US"
          },
          "provider": {
            "@type": "LegalService",
            "name": "Adler & Herbach Real Estate Attorneys",
            "url": "https://calculators.northsidelegal.com",
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "7321 Ridgeway Avenue",
              "addressLocality": "Skokie",
              "addressRegion": "IL",
              "postalCode": "60076",
              "addressCountry": "US"
            },
            "telephone": "+1 (847) 318-3770",
            "priceRange": "$750"
          }
        }}
      />

      <Helmet>
        <title>Home Buying Timeline Calculator | Purchase Journey Planner</title>
        <meta 
          name="description" 
          content="Create a personalized timeline for your home buying journey, tracking key milestones and stages of the purchase process" 
        />
        <meta 
          name="keywords" 
          content="home buying timeline, purchase process, home buying stages, real estate journey planner" 
        />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://northsidelegal.com/timeline-calculator" />
      </Helmet>

      <div className="max-w-4xl mx-auto">
        <div className="card">
          <div className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-2xl font-display font-bold text-gray-900">
                  Illinois Closing Timeline Calculator
                </h3>
                <p className="mt-2 text-gray-500">
                  Calculate important deadlines and milestones for your Illinois home purchase.
                </p>
              </div>
              <a href="tel:847-318-3770" className="btn-secondary">
                <PhoneIcon className="h-5 w-5 mr-2" />
                Get Help
              </a>
            </div>

            <form onSubmit={handleSubmit(onSubmit)} className="calculator-form mt-8">
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <div className="form-group">
                  <label htmlFor="currentStage" className="form-label">
                    Current Stage
                    <span className="tooltip ml-1">
                      <QuestionMarkCircleIcon className="h-4 w-4 inline text-gray-400" />
                      <span className="tooltip-content">
                        Select your current stage in the home buying process
                      </span>
                    </span>
                  </label>
                  <select
                    {...register('currentStage')}
                    className="select-field"
                    onChange={(e) => setSelectedStage(e.target.value)}
                  >
                    {stages.map((stage) => (
                      <option key={stage.value} value={stage.value}>
                        {stage.label}
                      </option>
                    ))}
                  </select>
                  {errors.currentStage && (
                    <p className="form-error">{errors.currentStage.message}</p>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="currentStageDate" className="form-label">
                    Date of Current Stage
                    <span className="tooltip ml-1">
                      <QuestionMarkCircleIcon className="h-4 w-4 inline text-gray-400" />
                      <span className="tooltip-content">
                        When did this stage begin?
                      </span>
                    </span>
                  </label>
                  <div className="relative">
                    <CalendarIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 z-10" />
                    <DatePicker
                      selected={selectedDate}
                      onChange={(date: Date) => {
                        setSelectedDate(date)
                        setValue('currentStageDate', format(date, 'yyyy-MM-dd'))
                      }}
                      dateFormat="MMM d, yyyy"
                      className="input-field pl-10 w-full cursor-pointer"
                      wrapperClassName="w-full"
                      showPopperArrow={false}
                      popperPlacement="bottom-start"
                      popperClassName="datepicker-popper"
                    />
                  </div>
                  {errors.currentStageDate && (
                    <p className="form-error">{errors.currentStageDate.message}</p>
                  )}
                </div>
              </div>

              {currentStageInfo && (
                <div className="mt-4 p-4 bg-brand-50 rounded-lg">
                  <div className="flex items-start">
                    <InformationCircleIcon className="h-5 w-5 text-brand-600 mt-0.5 mr-2" />
                    <div>
                      <h4 className="text-sm font-medium text-brand-800">{currentStageInfo.label}</h4>
                      <p className="mt-1 text-sm text-brand-600">{currentStageInfo.description}</p>
                    </div>
                  </div>
                </div>
              )}

              <div className="mt-6">
                <button type="submit" className="btn-primary w-full">
                  <ClockIcon className="h-5 w-5 mr-2" />
                  Calculate Timeline
                </button>
              </div>
            </form>

            {timeline.length > 0 && (
              <div ref={resultsRef} className="mt-8">
                <div className="bg-gray-50 rounded-lg p-4 mb-6">
                  <div className="flex items-center space-x-4">
                    <div className="flex items-center">
                      <div className="h-3 w-3 rounded-full bg-green-500 mr-2" />
                      <span className="text-sm text-gray-600">Completed</span>
                    </div>
                    <div className="flex items-center">
                      <div className="h-3 w-3 rounded-full bg-brand-600 mr-2" />
                      <span className="text-sm text-gray-600">Current</span>
                    </div>
                    <div className="flex items-center">
                      <div className="h-3 w-3 rounded-full bg-gray-100 mr-2" />
                      <span className="text-sm text-gray-600">Upcoming</span>
                    </div>
                  </div>
                </div>

                <div className="relative">
                  <div className="absolute top-0 bottom-0 left-4 w-0.5 bg-gray-200" />
                  <ul className="space-y-6">
                    {timeline.map((event, eventIdx) => (
                      <li key={event.id} className="relative">
                        <div className="relative flex items-start group">
                          <span className={`h-9 w-9 rounded-full flex items-center justify-center ring-8 ring-white ${getStatusColor(event.status)}`}>
                            {event.status === 'completed' ? (
                              <CheckCircleIcon className="h-5 w-5" />
                            ) : (
                              <ClockIcon className="h-5 w-5" />
                            )}
                          </span>
                          <div className="ml-4 min-w-0 flex-1">
                            <div className="text-sm font-semibold text-gray-900 flex items-center">
                              {event.name}
                              {event.status === 'current' && (
                                <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-brand-100 text-brand-800">
                                  Current Stage
                                </span>
                              )}
                            </div>
                            <p className="mt-1 text-sm text-gray-600">
                              {event.description}
                            </p>
                            {event.tips && (
                              <div className="mt-3 bg-gray-50 rounded-lg p-3">
                                <h5 className="text-sm font-medium text-gray-900 mb-2">Tips:</h5>
                                <ul className="space-y-1">
                                  {event.tips.map((tip, index) => {
                                    // More sophisticated icon and color selection
                                    let IconComponent = InformationCircleIcon;
                                    let iconColor = 'text-brand-500';

                                    // Specific keywords to trigger different icons
                                    const warningKeywords = ['verify', 'careful', 'monitor', 'issues', 'gaps'];
                                    const importantKeywords = ['immediately', 'promptly', 'required', 'carefully'];
                                    const generalTipKeywords = ['prepare', 'schedule', 'document', 'review'];

                                    const lowercaseTip = tip.toLowerCase();

                                    if (warningKeywords.some(keyword => lowercaseTip.includes(keyword))) {
                                      IconComponent = ExclamationTriangleIcon;
                                      iconColor = 'text-red-600';
                                    } else if (importantKeywords.some(keyword => lowercaseTip.includes(keyword))) {
                                      IconComponent = LightBulbIcon;
                                      iconColor = 'text-yellow-600';
                                    } else if (generalTipKeywords.some(keyword => lowercaseTip.includes(keyword))) {
                                      IconComponent = QuestionMarkCircleIcon;
                                      iconColor = 'text-green-600';
                                    }

                                    return (
                                      <li key={index} className="text-sm text-gray-600 flex items-start">
                                        <IconComponent 
                                          className={`h-5 w-5 mr-2 mt-0.5 flex-shrink-0 ${iconColor}`} 
                                        />
                                        {tip}
                                      </li>
                                    );
                                  })}
                                </ul>
                              </div>
                            )}
                            <div className="mt-2 text-sm text-gray-500">
                              <time dateTime={format(event.date, 'yyyy-MM-dd')}>
                                {format(event.date, 'MMM d, yyyy')}
                              </time>
                              <span className="mx-1">•</span>
                              <span>
                                {event.daysFromStart === 0
                                  ? 'Start'
                                  : `+${event.daysFromStart} days`}
                              </span>
                            </div>
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
